import * as Sentry from '@sentry/react';

import {
  ADDRESS_PREQUAL_ENDPOINT,
  ADDRESS_SERVICE_URL,
  CHANNELS_JSON_CONFIG_URL,
  JSON_CONFIG_URL,
  REACT_APP_ENDPOINT,
  REACT_APP_MSL_CT_ENDPOINT,
} from '@/lib/config';
import { ERROR_TYPE, ISSUE_PRIORITY } from '@/lib/constants';

export const IS_SENTRY_SERVICE_ENABLED = true;
export const SCRIPT_ERROR = 'Script error.';
export const ERR_TUNNEL_CONNECTION_FAILED = 'ERR_TUNNEL_CONNECTION_FAILED';
export const LEVEL = ['error'];
const PRODUCTION_TRACE_SAMPLE_RATE = 0.1;
const PRELIVE_TRACE_SAMPLE_RATE = 0.25;

type RequestTarget = RequestInfo | URL | null;
type RequestMetadata = {
  method?: unknown;
  url?: unknown;
};
type SpanData = {
  data?: Record<string, unknown>;
  description?: unknown;
};

const COMMERCE_TOOLS_SERVICE = 'CommerceToolsService';

const getCommerceToolsBaseUrl = () =>
  process.env.REACT_APP_MSL_CT_ENDPOINT || REACT_APP_MSL_CT_ENDPOINT || '';

const getTrackedFetchBaseUrls = () =>
  [
    ADDRESS_PREQUAL_ENDPOINT,
    ADDRESS_SERVICE_URL,
    CHANNELS_JSON_CONFIG_URL,
    JSON_CONFIG_URL,
    REACT_APP_ENDPOINT,
    getCommerceToolsBaseUrl(),
    process.env.REACT_APP_GRAPHQL_URL,
    process.env.REACT_APP_EPG_DIGITAL_API,
  ].filter(Boolean) as string[];

const getTracePropagationTargets = () => ['localhost'];

export class SentryService {
  private readonly dsn?: string;
  private env: string;
  private denyUrls?: RegExp[]; // see https://docs.sentry.io/platforms/javascript/configuration/filtering/
  private isFetchTracingInitialized = false;
  private isGlobalErrorTracingInitialized = false;
  private isXHRTracingInitialized = false;

  constructor(dsn?: string, env?: string, denyUrls?: RegExp[]) {
    this.dsn = dsn;
    this.env = (env || 'production').trim().toLowerCase();
    this.denyUrls = denyUrls;
  }

  public init = () => {
    if (!this.isSentryServiceEnabled()) return;

    Sentry.init({
      dsn: this.dsn,
      environment: this.env,
      denyUrls: this.denyUrls,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.globalHandlersIntegration({ onerror: true, onunhandledrejection: true }),
        Sentry.captureConsoleIntegration({
          levels: LEVEL,
        }),
        Sentry.extraErrorDataIntegration({ depth: 10 }),
      ],
      // Capture a representative production sample while retaining additional coverage in prelive.
      tracesSampler: () =>
        this.env === 'prelive' ? PRELIVE_TRACE_SAMPLE_RATE : PRODUCTION_TRACE_SAMPLE_RATE,
      beforeSendSpan: span => this.enrichSpan(span),
      tracePropagationTargets: getTracePropagationTargets(), // Ensure Sky and Commerce Tools API requests participate in tracing
    });
  };

  private getWindowLocation() {
    if (typeof globalThis.globalThis.window === 'undefined') return null;

    try {
      return globalThis.globalThis.window.location ?? null;
    } catch {
      return null;
    }
  }

  private isRunningOnLocalhost() {
    const hostname = this.getWindowLocation()?.hostname;

    // Tests and partially mocked browser environments may not provide a full location object.
    // Treat those cases as non-hosted so Sentry stays disabled instead of throwing.
    if (!hostname) {
      return true;
    }

    return ['localhost', '127.0.0.1', '::1', '[::1]'].includes(hostname);
  }

  private canSendEvents() {
    if (!IS_SENTRY_SERVICE_ENABLED || !this.dsn) return false;

    if (this.isRunningOnLocalhost()) {
      // Keep localhost sessions out of Sentry. If you need local Sentry testing,
      // temporarily change this branch to return true.
      return false;
    }

    if (['prod', 'production'].includes(this.env)) {
      return true;
    }

    if (this.env === 'prelive') {
      return true;
    }

    return false;
  }

  private normalizeError(error: unknown, fallbackMessage: string) {
    if (error instanceof Error) {
      return error;
    }

    const message =
      typeof error === 'string'
        ? error
        : typeof error === 'object' && error !== null && 'message' in error
          ? String((error as { message?: unknown }).message)
          : fallbackMessage;

    return new Error(message || fallbackMessage);
  }

  private getRequestUrlValue(input?: RequestTarget) {
    if (!input) return null;

    if (typeof input === 'string') {
      return input;
    }

    if (input instanceof URL) {
      return input.toString();
    }

    if (typeof Request !== 'undefined' && input instanceof Request) {
      return input.url;
    }

    if (
      typeof input === 'object' &&
      'url' in input &&
      typeof (input as RequestMetadata).url === 'string'
    ) {
      return (input as { url: string }).url;
    }

    return null;
  }

  private resolveRequestUrl(input?: RequestTarget) {
    const requestUrl = this.getRequestUrlValue(input);
    if (!requestUrl) return null;

    const locationOrigin = this.getWindowLocation()?.origin;

    try {
      return locationOrigin ? new URL(requestUrl, locationOrigin) : new URL(requestUrl);
    } catch {
      return null;
    }
  }

  private shouldTraceFetchRequest(input?: RequestTarget) {
    const requestUrl = this.resolveRequestUrl(input);
    const locationOrigin = this.getWindowLocation()?.origin;
    if (!requestUrl || !locationOrigin) return false;

    if (requestUrl.origin === locationOrigin && requestUrl.pathname.startsWith('/api/')) {
      return true;
    }

    return getTrackedFetchBaseUrls().some(baseUrl => {
      try {
        const trackedUrl = new URL(baseUrl, locationOrigin);
        const trackedPath =
          trackedUrl.pathname === '/' ? '' : trackedUrl.pathname.replace(/\/$/, '');

        return (
          requestUrl.origin === trackedUrl.origin &&
          (trackedPath === '' || requestUrl.pathname.startsWith(trackedPath))
        );
      } catch {
        return false;
      }
    });
  }

  private resolveConfiguredUrl(rawUrl?: string | null) {
    if (!rawUrl) return null;

    try {
      return new URL(rawUrl);
    } catch {
      return null;
    }
  }

  private getSpanUrlValue(span?: SpanData) {
    const candidates = [
      span?.data?.url,
      span?.data?.['url.full'],
      span?.data?.['http.url'],
      span?.data?.['http.request.url'],
      span?.data?.['resource.url'],
      span?.description,
    ];

    return (
      candidates.find((candidate): candidate is string => typeof candidate === 'string') ?? null
    );
  }

  private getCommerceToolsMetadata(urlValue?: string | null) {
    const requestUrl = this.resolveConfiguredUrl(urlValue);
    const baseUrl = this.resolveConfiguredUrl(getCommerceToolsBaseUrl());

    if (!requestUrl || !baseUrl) return null;

    const basePath = baseUrl.pathname.replace(/\/$/, '');
    const requestPath = requestUrl.pathname;

    if (requestUrl.origin !== baseUrl.origin) return null;
    if (!requestPath.startsWith(basePath)) return null;

    const endpointSuffix = requestPath.slice(basePath.length) || '/';
    const endpoint = endpointSuffix.startsWith('/') ? endpointSuffix : `/${endpointSuffix}`;

    return {
      endpoint,
      url: `${requestUrl.origin}${requestPath}`,
    };
  }

  private enrichSpan<T extends SpanData>(span: T): T {
    const commerceToolsMetadata = this.getCommerceToolsMetadata(this.getSpanUrlValue(span));

    if (!commerceToolsMetadata) {
      return span;
    }

    const requestMethod =
      span.data?.['http.request.method'] ?? span.data?.method ?? span.data?.['http.method'];

    span.data = {
      ...span.data,
      service: COMMERCE_TOOLS_SERVICE,
      'ct.endpoint': commerceToolsMetadata.endpoint,
      'ct.url': commerceToolsMetadata.url,
      ...(typeof requestMethod === 'string' ? { 'ct.method': requestMethod.toUpperCase() } : {}),
    };

    return span;
  }

  private resolveRequestMethod(input?: RequestTarget, init?: RequestInit | null) {
    const requestMethod =
      init?.method ??
      (typeof Request !== 'undefined' && input instanceof Request ? input.method : undefined) ??
      (typeof input === 'object' &&
      input !== null &&
      'method' in input &&
      typeof (input as RequestMetadata).method === 'string'
        ? String((input as { method: string }).method)
        : undefined);

    return (requestMethod || 'GET').toUpperCase();
  }

  private getSanitizedRequestUrl(input?: RequestTarget) {
    const requestUrl = this.resolveRequestUrl(input);

    if (requestUrl) {
      return `${requestUrl.origin}${requestUrl.pathname}`;
    }

    const rawInput = this.getRequestUrlValue(input);
    if (!rawInput) {
      return 'unknown-url';
    }

    try {
      const locationOrigin = this.getWindowLocation()?.origin;
      const fallbackUrl = locationOrigin ? new URL(rawInput, locationOrigin) : new URL(rawInput);
      return `${fallbackUrl.origin}${fallbackUrl.pathname}`;
    } catch {
      return rawInput.split('?')[0].split('#')[0];
    }
  }

  private captureFetchFailure(
    requestInput: RequestInfo | URL | undefined,
    init: RequestInit | undefined,
    error: Error,
    responseDetails?: {
      status?: number;
      statusText?: string;
      url?: string;
    }
  ) {
    const method = this.resolveRequestMethod(requestInput, init);
    const endpoint = this.getSanitizedRequestUrl(responseDetails?.url || requestInput);
    const status = responseDetails?.status;
    const statusText = responseDetails?.statusText?.trim();
    let detailedMessage = `Fetch ${method} ${endpoint} failed: ${error.message}`;

    if (typeof status === 'number') {
      const statusDescription = statusText ? `${status} ${statusText}` : String(status);
      detailedMessage = `Fetch ${method} ${endpoint} failed with status ${statusDescription}`;
    }

    const detailedError = new Error(detailedMessage);
    detailedError.name = error.name || 'Error';

    this.captureException(
      detailedError,
      this.errorInfoDetails(
        detailedError,
        ERROR_TYPE.ERROR,
        {
          eventTitle: 'Fetch Failed',
          endPoint: endpoint,
          method,
          ...(typeof status === 'number' ? { status: String(status) } : {}),
        },
        {
          request: {
            method,
            url: endpoint,
          },
          ...(typeof status === 'number'
            ? {
                response: {
                  status,
                  statusText: statusText || 'Unknown',
                },
              }
            : {}),
        },
        {
          url: endpoint,
          method,
          status,
          statusText,
          originalErrorMessage: error.message,
          originalErrorName: error.name,
          online:
            typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
              ? navigator.onLine
              : undefined,
        }
      )
    );
  }

  private isAbortedFetchError(error: unknown) {
    return this.normalizeError(error, 'Fetch aborted').name === 'AbortError';
  }

  /*
   const errorInfo = {
      level: 'error',
      extra: {
        errorMessage: footerError.message,
        name: footerError.name,
        host: footerError.host,
      },
      tags: {
        eventTitle: footerError.name,
        endPoint: footerError.name,
      },
      message: footerError.message,
      transactionName: footerError.message,
    };
    sentryService?.captureException(`${footerError.message} - ${footerError.sysId}`, errorInfo);
  */

  public captureException(err: unknown, contexts?: any) {
    if (!this.canSendEvents()) return;
    Sentry.captureException(this.normalizeError(err, 'Unexpected error'), contexts);
  }

  /* Example 1: 
  sentryService.captureMessage('Error',
    {
      extra: {
        message: 'Unhandled API Request Error',
      },
    },
  );*/
  public captureMessage(message: any, SeverityLevel: any) {
    if (!this.canSendEvents()) return;
    Sentry.captureMessage(message, SeverityLevel);
  }

  // adding additional information to the error object to check details at sentry dashboard.
  /*const errorInfo = {
      level: errorType,
      extra: {
        errorMessage: error.message,
        name: error.name,
        host: error.host,
      },
      context: {
        issueDetails: {
          priority: priority,
          description: error.message,
        },
      },
      tags: {
        eventTitle: error.name,
        endPoint: error.name,
      },
      message: error.message,
      transactionName: error.message,
    };*/

  public errorInfoDetails(
    error: any,
    errorType: string,
    tagsDetails?: any,
    contextDetails?: any,
    extraDetails?: any
  ) {
    const normalizedError = this.normalizeError(error, 'Unexpected error');
    let errorInfo: any = {};
    errorInfo = {
      level: errorType,
      extra: {
        errorMessage: normalizedError.message,
        name: normalizedError.name,
        host: error?.host,
      },
    };
    if (tagsDetails) {
      errorInfo = {
        ...errorInfo,
        tags: tagsDetails,
      };
    }
    if (contextDetails) {
      errorInfo = {
        ...errorInfo,
        contexts: contextDetails,
      };
    }
    if (extraDetails) {
      errorInfo = {
        ...errorInfo,
        extra: {
          ...errorInfo.extra,
          ...extraDetails,
        },
      };
    }
    return errorInfo;
  }

  public isSentryServiceEnabled() {
    return this.canSendEvents();
  }

  // To trace the network tab - fetch failure status captured with the below function
  public traceFetchNetworkStatusFailed() {
    if (
      !this.canSendEvents() ||
      typeof globalThis.globalThis.window === 'undefined' ||
      this.isFetchTracingInitialized
    ) {
      return;
    }

    this.isFetchTracingInitialized = true;

    const originalFetch = globalThis.window.fetch.bind(globalThis.window);
    globalThis.window.fetch = async (...args: Parameters<typeof fetch>) => {
      const [requestInput, requestInit] = args;
      const shouldTraceRequest = this.shouldTraceFetchRequest(requestInput);

      try {
        const response = await originalFetch(...args);
        if (shouldTraceRequest && !response.ok) {
          this.captureFetchFailure(
            requestInput,
            requestInit,
            new Error(`HTTP ${response.status}`),
            {
              url: response.url,
              status: response.status,
              statusText: response.statusText,
            }
          );
        }
        return response;
      } catch (error) {
        if (shouldTraceRequest && !this.isAbortedFetchError(error)) {
          this.captureFetchFailure(
            requestInput,
            requestInit,
            this.normalizeError(error, 'Fetch request failed')
          );
        }
        throw error;
      }
    };
  }

  // To trace the network tab - XHR failure status captured with the below function
  public traceXHRNetworkStatusFailed() {
    if (
      !this.canSendEvents() ||
      typeof globalThis.window === 'undefined' ||
      this.isXHRTracingInitialized
    ) {
      return;
    }

    this.isXHRTracingInitialized = true;

    const originalXHR = globalThis.window.XMLHttpRequest;
    const xhr = new originalXHR();
    xhr.addEventListener('readystatechange', () => {
      if (xhr.readyState === 4 && xhr.status >= 400) {
        this.captureException(new Error(`XHR ${xhr.status} ${xhr.statusText} failed`), {
          level: ERROR_TYPE.ERROR,
          extra: {
            url: xhr.responseURL,
            status: xhr.status,
            statusText: xhr.statusText,
          },
        });
      }
    });
  }

  // To trace the - Error failure status captured with the below function
  public traceGlobalError() {
    if (
      !this.canSendEvents() ||
      typeof globalThis.window === 'undefined' ||
      this.isGlobalErrorTracingInitialized
    ) {
      return;
    }

    this.isGlobalErrorTracingInitialized = true;

    globalThis.window.addEventListener('error', event => {
      if (event.error && event.error.name !== '') {
        this.captureException(event.error);
      }
      if (
        event.message &&
        (event.message === SCRIPT_ERROR || event.message.includes(ERR_TUNNEL_CONNECTION_FAILED))
      ) {
        const error = new Error();
        error.message =
          event.message === SCRIPT_ERROR
            ? SCRIPT_ERROR
            : `Network Error: ${ERR_TUNNEL_CONNECTION_FAILED}`;
        error.stack =
          event.message === SCRIPT_ERROR
            ? SCRIPT_ERROR
            : `Network Error: ${ERR_TUNNEL_CONNECTION_FAILED}`;

        const errorInfo = this.errorInfoDetails(
          error,
          ERROR_TYPE.ERROR,
          undefined,
          undefined,
          event
        );
        this.captureException(error, errorInfo);
      }
    });
  }
  public captureAPIErrors(contentError: any, api: string, endpoint: string) {
    const context = {
      issueDetails: {
        priority: ISSUE_PRIORITY.HIGH,
        description: contentError.message,
      },
    };
    const tags = {
      eventTitle: api,
      endPoint: endpoint,
    };
    const errorInfo = this.errorInfoDetails(contentError, ERROR_TYPE.ERROR, tags, context, {
      sysId: contentError?.sysId,
    });
    this.captureException(contentError, errorInfo);
  }
}
